/* global axios */
import ApiClient from './ApiClient';
import { requestConfig, resourcePath } from './lais/httpHelpers';
import { redactLaisPayload } from './lais/redaction';

const HISTORY_PAYLOAD_MARKERS = [
  'summary',
  'lead',
  'buyer_profile',
  'qualification_questions',
  'offers',
  'favorites',
  'recommendations',
  'timeline',
  'crm_operations',
  'origem',
  'public_visit_request',
  'selected_conversation',
  'conversation_feedback',
];

const CRM_SYNC_PAYLOAD_KEYS = [
  'payload',
  'response',
  'raw_payload',
  'rawPayload',
  'request_payload',
  'requestPayload',
  'metadata',
];

const isObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const LEAD_HISTORY_LOAD_STATES = Object.freeze({
  LOADING: 'loading',
  READY: 'ready',
  EMPTY: 'empty',
  ERROR: 'error',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'not_found',
});

const DEFAULT_ERROR_MESSAGES = {
  [LEAD_HISTORY_LOAD_STATES.FORBIDDEN]:
    'Sem permissão para carregar este histórico.',
  [LEAD_HISTORY_LOAD_STATES.NOT_FOUND]: 'Histórico não encontrado.',
  [LEAD_HISTORY_LOAD_STATES.ERROR]: 'Não foi possível carregar este histórico.',
};

const statusFromError = error => {
  const status = Number(error?.response?.status || error?.status);
  return Number.isFinite(status) && status > 0 ? status : null;
};

const messageFromError = error =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  '';

export const classifyLeadHistoryError = error => {
  const status = statusFromError(error);
  const kind =
    {
      403: LEAD_HISTORY_LOAD_STATES.FORBIDDEN,
      404: LEAD_HISTORY_LOAD_STATES.NOT_FOUND,
    }[status] || LEAD_HISTORY_LOAD_STATES.ERROR;

  return {
    kind,
    status,
    message: messageFromError(error) || DEFAULT_ERROR_MESSAGES[kind],
  };
};

const isHistoryPayloadWrapper = value =>
  isObject(value?.payload) &&
  (Object.keys(value).length === 1 ||
    HISTORY_PAYLOAD_MARKERS.some(key => value.payload[key] !== undefined));

const normalizeCrmSyncAttempt = attempt => {
  if (!isObject(attempt)) return attempt;

  const normalized = { ...attempt };
  CRM_SYNC_PAYLOAD_KEYS.forEach(key => {
    if (normalized[key] !== undefined) {
      normalized[key] = redactLaisPayload(normalized[key]);
    }
  });
  return normalized;
};

export const normalizeLeadHistoryContact = contact => {
  if (!isObject(contact)) return contact || {};

  const normalized = { ...contact };
  if (Array.isArray(normalized.crm_sync_attempts)) {
    normalized.crm_sync_attempts = normalized.crm_sync_attempts.map(
      normalizeCrmSyncAttempt
    );
  }

  if (isObject(normalized.additional_attributes)) {
    normalized.additional_attributes = { ...normalized.additional_attributes };
    if (Array.isArray(normalized.additional_attributes.crm_sync_attempts)) {
      normalized.additional_attributes.crm_sync_attempts =
        normalized.additional_attributes.crm_sync_attempts.map(
          normalizeCrmSyncAttempt
        );
    }
  }

  return normalized;
};

export const normalizeLeadHistoryPayload = payload => {
  const source = isHistoryPayloadWrapper(payload) ? payload.payload : payload;
  if (!isObject(source)) return {};

  const normalized = { ...source };
  if (normalized.lead !== undefined) {
    normalized.lead = normalizeLeadHistoryContact(normalized.lead);
  }
  return normalized;
};

const hasRenderablePayloadValue = value => {
  if (Array.isArray(value)) return value.length > 0;
  if (isObject(value)) return Object.keys(value).length > 0;
  return value !== undefined && value !== null && value !== '';
};

export const hasLeadHistoryPayloadData = payload =>
  Object.values(normalizeLeadHistoryPayload(payload)).some(
    hasRenderablePayloadValue
  );

export const getLeadHistoryPayloadState = payload =>
  hasLeadHistoryPayloadData(payload)
    ? LEAD_HISTORY_LOAD_STATES.READY
    : LEAD_HISTORY_LOAD_STATES.EMPTY;

const withLeadHistoryRequestState = request =>
  request.catch(error => {
    if (isObject(error)) {
      error.leadHistoryState = classifyLeadHistoryError(error);
    }
    throw error;
  });

const withNormalizedHistoryPayload = request =>
  withLeadHistoryRequestState(request).then(response => ({
    ...response,
    data: {
      ...(isObject(response.data) ? response.data : {}),
      payload: normalizeLeadHistoryPayload(response.data?.payload),
    },
  }));

const withNormalizedContactPayload = request =>
  withLeadHistoryRequestState(request).then(response => ({
    ...response,
    data: {
      ...(isObject(response.data) ? response.data : {}),
      payload: normalizeLeadHistoryContact(response.data?.payload),
    },
  }));

class LeadHistoryAPI extends ApiClient {
  constructor() {
    super('history', { accountScoped: true });
  }

  get accountUrl() {
    return this.baseUrl();
  }

  show(params = {}) {
    return withNormalizedHistoryPayload(
      axios.get(this.url, requestConfig(params))
    );
  }

  contact(contactId) {
    return withNormalizedContactPayload(
      axios.get(
        resourcePath(this.accountUrl, 'contacts', contactId),
        requestConfig()
      )
    );
  }

  conversations(contactId) {
    return withLeadHistoryRequestState(
      axios.get(
        resourcePath(this.accountUrl, 'contacts', contactId, 'conversations'),
        requestConfig()
      )
    );
  }

  visits(params = {}) {
    return withLeadHistoryRequestState(
      axios.get(resourcePath(this.accountUrl, 'visitas'), requestConfig(params))
    );
  }

  messages(conversationId, params = {}) {
    return withLeadHistoryRequestState(
      axios.get(
        resourcePath(
          this.accountUrl,
          'conversations',
          conversationId,
          'messages'
        ),
        requestConfig(params)
      )
    );
  }

  notes(contactId) {
    return withLeadHistoryRequestState(
      axios.get(
        resourcePath(this.accountUrl, 'contacts', contactId, 'notes'),
        requestConfig()
      )
    );
  }

  createNote(contactId, content) {
    return withLeadHistoryRequestState(
      axios.post(
        resourcePath(this.accountUrl, 'contacts', contactId, 'notes'),
        { content },
        requestConfig()
      )
    );
  }

  createQualificationAnswer(contactId, payload = {}) {
    return withLeadHistoryRequestState(
      axios.post(
        resourcePath(
          this.accountUrl,
          'contacts',
          contactId,
          'qualification_answers'
        ),
        payload,
        requestConfig()
      )
    );
  }

  submitFeedback(payload = {}) {
    return withLeadHistoryRequestState(
      axios.post(resourcePath(this.url, 'feedback'), payload, requestConfig())
    );
  }

  updateContact(contactId, payload = {}) {
    return withLeadHistoryRequestState(
      axios.patch(
        resourcePath(this.accountUrl, 'contacts', contactId),
        payload,
        requestConfig()
      )
    );
  }
}

export default new LeadHistoryAPI();
